
import { ReactNode, createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { LoadingStateManager } from "@/utils/loadingStateManager";
import { cacheManager } from "@/utils/cacheManager";
import { useQueryClient } from "@tanstack/react-query";
import { permissionManager, SyncState } from "@/utils/permissions/PermissionManager";
import { useSessionRefresh } from '@/hooks/useSessionRefresh';
import { getPersistenceType, PERSISTENCE_KEY, clearAuthStorage } from '@/utils/auth/sessionManager';

// Define specific auth error types for better error handling
export type AuthErrorType = 
  | 'invalid_credentials'
  | 'user_not_found'
  | 'email_taken'
  | 'weak_password'
  | 'expired_session'
  | 'network_error'
  | 'rate_limited'
  | 'server_error'
  | 'unknown_error';

// Map Supabase error codes to our custom error types
const mapErrorToType = (error: any): AuthErrorType => {
  const errorCode = error?.code;
  const errorMessage = error?.message?.toLowerCase() || '';
  
  if (errorMessage.includes('invalid login credentials') || errorCode === 'auth/invalid-credential') {
    return 'invalid_credentials';
  } else if (errorMessage.includes('user not found') || errorCode === 'auth/user-not-found') {
    return 'user_not_found';
  } else if (errorMessage.includes('email already') || errorCode === 'auth/email-already-in-use') {
    return 'email_taken';
  } else if (errorMessage.includes('password') && errorMessage.includes('strong')) {
    return 'weak_password';
  } else if (errorMessage.includes('expired') || errorCode === 'auth/session-expired') {
    return 'expired_session';
  } else if (errorMessage.includes('network') || errorCode === 'auth/network-request-failed') {
    return 'network_error';
  } else if (errorMessage.includes('too many requests') || errorCode === 'auth/too-many-requests') {
    return 'rate_limited';
  } else if (errorCode?.startsWith('5') || errorMessage.includes('server')) {
    return 'server_error';
  }
  
  return 'unknown_error';
};

// Get user-friendly error messages with resolution steps
const getErrorMessage = (errorType: AuthErrorType): { title: string; description: string; action?: string } => {
  switch (errorType) {
    case 'invalid_credentials':
      return {
        title: 'Invalid Credentials',
        description: 'The email or password you provided is incorrect.',
        action: 'Please double-check your email and password and try again.'
      };
    case 'user_not_found':
      return {
        title: 'User Not Found',
        description: 'We couldn\'t find an account with that email address.',
        action: 'Would you like to create a new account instead?'
      };
    case 'email_taken':
      return {
        title: 'Email Already Registered',
        description: 'This email address is already associated with an account.',
        action: 'Please try signing in instead or use a different email address.'
      };
    case 'weak_password':
      return {
        title: 'Password Too Weak',
        description: 'Your password doesn\'t meet the security requirements.',
        action: 'Please use a stronger password with at least 8 characters, including numbers and symbols.'
      };
    case 'expired_session':
      return {
        title: 'Session Expired',
        description: 'Your session has expired for security reasons.',
        action: 'Please sign in again to continue.'
      };
    case 'network_error':
      return {
        title: 'Network Error',
        description: 'Unable to connect to authentication servers.',
        action: 'Please check your internet connection and try again.'
      };
    case 'rate_limited':
      return {
        title: 'Too Many Attempts',
        description: 'You\'ve made too many authentication attempts in a short period.',
        action: 'Please wait a few minutes before trying again.'
      };
    case 'server_error':
      return {
        title: 'Server Error',
        description: 'Our authentication service is experiencing issues.',
        action: 'Please try again later. We\'re working to resolve this issue.'
      };
    default:
      return {
        title: 'Authentication Error',
        description: 'An unexpected error occurred during authentication.',
        action: 'Please try again later or contact support if the issue persists.'
      };
  }
};

type UserWithRole = User & {
  role?: string;
};

// Key for storing role info in session storage
const ROLE_CACHE_KEY = 'auth_user_role';

interface AuthContextType {
  session: Session | null;
  user: UserWithRole | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any; errorType?: AuthErrorType } | undefined>;
  signUp: (email: string, password: string) => Promise<{ error: any; errorType?: AuthErrorType } | undefined>;
  signInWithLinkedIn: () => Promise<{ error: any; errorType?: AuthErrorType } | undefined>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  lastRefreshed: Date | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Define auth state events for better state management
type AuthEvent = 
  | 'SIGN_IN'
  | 'SIGN_OUT'
  | 'REFRESH'
  | 'INITIALIZE'
  | 'USER_UPDATED';

// Define auth state machine states
type AuthState = 
  | 'IDLE'
  | 'INITIALIZING'
  | 'AUTHENTICATED'
  | 'UNAUTHENTICATED'
  | 'REFRESHING'
  | 'ERROR';

// Track request identifiers to prevent duplicate requests
const processedRequests = new Set<string>();
const REQUEST_DEBOUNCE_MS = 1000; // 1 second debounce

// Generate a unique ID for each request
const generateRequestId = (type: AuthEvent, userId?: string) => {
  return `${type}_${userId || 'anonymous'}_${Date.now()}`;
};

// Clear old requests periodically
const clearOldRequests = () => {
  processedRequests.clear();
};

// Set interval to clear old requests every 5 minutes
setInterval(clearOldRequests, 5 * 60 * 1000);

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserWithRole | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Refs for managing component lifecycle and auth state
  const isActive = useRef(true);
  const initializedRef = useRef(false);
  const authStateRef = useRef<AuthState>('IDLE');
  const pendingEventsRef = useRef<{type: AuthEvent, data?: any, requestId?: string}[]>([]);
  const processingEventRef = useRef(false);
  const oauthRedirectInProgressRef = useRef(false);
  const retryCountRef = useRef(0);
  const roleUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Function to check if a request has already been processed or should be debounced
  const shouldProcessRequest = useCallback((requestId: string, type: AuthEvent): boolean => {
    // If we've seen this exact request ID, skip it
    if (processedRequests.has(requestId)) {
      console.log(`Skipping duplicate request: ${requestId}`);
      return false;
    }
    
    // Add to processed requests
    processedRequests.add(requestId);
    
    // Special case for events that should be rate limited
    if (type === 'REFRESH' || type === 'USER_UPDATED') {
      // Find similar recent requests
      const similarRequestPattern = `${type}_`;
      const recentSimilarRequests = Array.from(processedRequests)
        .filter(id => id.startsWith(similarRequestPattern) && 
                     id !== requestId && 
                     Date.now() - parseInt(id.split('_')[2]) < REQUEST_DEBOUNCE_MS);
      
      // If we have recent similar requests, debounce this one
      if (recentSimilarRequests.length > 0) {
        console.log(`Debouncing ${type} request as similar request was processed recently`);
        return false;
      }
    }
    
    return true;
  }, []);
  
  // Log authentication events with improved details
  const logAuthEvent = useCallback((event: string, data?: any, error?: any) => {
    if (!isActive.current) return;
    
    const timestamp = new Date().toISOString();
    const userId = user?.id || 'unauthenticated';
    const currentAuthState = authStateRef.current;
    
    console.log(`[Auth Event ${timestamp}] ${event} - User: ${userId}, State: ${currentAuthState}, Pending Events: ${pendingEventsRef.current.length}`);
    
    if (data) {
      console.log('Data:', data);
    }
    
    if (error) {
      console.error('Error:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
    }
  }, [user]);
  
  // Save role information to session storage
  const saveRoleToCache = useCallback((userId: string, role: string) => {
    try {
      const cacheData = {
        userId,
        role,
        timestamp: Date.now()
      };
      sessionStorage.setItem(ROLE_CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      // Ignore errors in private browsing mode
    }
  }, []);
  
  // Get cached role information
  const getCachedRole = useCallback((userId: string): string | null => {
    try {
      const cacheDataStr = sessionStorage.getItem(ROLE_CACHE_KEY);
      if (cacheDataStr) {
        const cacheData = JSON.parse(cacheDataStr);
        // Only use cache if it's for the same user and not too old (5 minutes)
        if (cacheData.userId === userId && 
            Date.now() - cacheData.timestamp < 5 * 60 * 1000) {
          return cacheData.role;
        }
      }
      return null;
    } catch (e) {
      return null;
    }
  }, []);
  
  // Fetch user role with improved error handling and caching
  const fetchUserRole = useCallback(async (userId: string): Promise<string | null> => {
    const maxRetries = 3;
    let retryCount = 0;
    let lastError: any = null;
    
    // First check cache
    const cachedRole = getCachedRole(userId);
    if (cachedRole) {
      logAuthEvent(`USING_CACHED_ROLE`, { userId, role: cachedRole });
      return cachedRole;
    }
    
    while (retryCount < maxRetries) {
      try {
        logAuthEvent(`FETCHING_USER_ROLE`, { userId, attempt: retryCount + 1 });
        
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();
          
        if (roleError) {
          logAuthEvent('ROLE_FETCH_ERROR', { userId }, roleError);
          lastError = roleError;
          retryCount++;
          
          // Exponential backoff with jitter
          const delay = Math.min(1000 * (2 ** retryCount) + Math.random() * 100, 5000);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        const role = roleData?.role || 'user';
        logAuthEvent('ROLE_FETCH_SUCCESS', { userId, role });
        
        // Cache the role
        saveRoleToCache(userId, role);
        
        return role;
      } catch (error) {
        logAuthEvent('ROLE_FETCH_EXCEPTION', { userId, attempt: retryCount + 1 }, error);
        lastError = error;
        retryCount++;
        
        // Exponential backoff with jitter
        const delay = Math.min(1000 * (2 ** retryCount) + Math.random() * 100, 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // After all retries failed, log a detailed error
    logAuthEvent('ROLE_FETCH_ALL_RETRIES_FAILED', { 
      userId, 
      maxRetries,
      syncState: permissionManager.getSyncState()
    }, lastError);
    
    // Return default role after all retries failed
    return 'user';
  }, [logAuthEvent, getCachedRole, saveRoleToCache]);
  
  // Atomic state update function with improved coordination and role sync
  const updateAuthState = useCallback(async (
    newSession: Session | null,
    newUser: User | null,
    shouldFetchRole = true
  ) => {
    if (!isActive.current) return;
    
    // Don't update if nothing has changed and we already have consistent state
    if (!newSession && !newUser && !session && !user) {
      return;
    }

    try {
      let userWithRole: UserWithRole | null = null;
      let roleChanged = false;
      
      if (newUser) {
        // If we need to fetch the role
        if (shouldFetchRole) {
          try {
            // Cancel any existing role update timeout
            if (roleUpdateTimeoutRef.current) {
              clearTimeout(roleUpdateTimeoutRef.current);
              roleUpdateTimeoutRef.current = null;
            }
            
            // Log sync state before role fetch
            logAuthEvent('ROLE_SYNC_STARTED', {
              userId: newUser.id,
              permissionSyncState: permissionManager.getSyncState(),
              permissionStateVersion: permissionManager.getStateVersion()
            });
            
            // Check cache first
            const cachedRole = getCachedRole(newUser.id);
            let role: string | null;
            
            if (cachedRole) {
              // Use cached role immediately
              role = cachedRole;
              logAuthEvent('USING_CACHED_ROLE', { userId: newUser.id, role });
              
              // Schedule a background refresh of the role
              roleUpdateTimeoutRef.current = setTimeout(async () => {
                const freshRole = await fetchUserRole(newUser.id);
                if (freshRole && freshRole !== cachedRole) {
                  // Role has changed, update state
                  logAuthEvent('CACHED_ROLE_UPDATED', { userId: newUser.id, oldRole: cachedRole, newRole: freshRole });
                  const updatedUser = { ...newUser, role: freshRole };
                  setUser(updatedUser);
                  
                  // Update permission manager with fresh role
                  await permissionManager.updateUserRoleWithRetry(freshRole as any, newUser.id);
                }
              }, 2000); // Refresh after 2 seconds
            } else {
              // No cache, fetch role now
              role = await fetchUserRole(newUser.id);
            }
            
            if (role) {
              userWithRole = { ...newUser, role };
              
              // Check if role has changed from current state
              roleChanged = user?.role !== role;
              
              // Update permission manager with user role
              const syncSuccess = await permissionManager.updateUserRoleWithRetry(role as any, newUser.id);
              
              // Log sync result
              logAuthEvent('ROLE_SYNC_COMPLETED', {
                userId: newUser.id,
                role,
                syncSuccess,
                permissionSyncState: permissionManager.getSyncState(),
                permissionStateVersion: permissionManager.getStateVersion(),
                roleChanged
              });
            } else {
              // Role fetch returned null after retries
              userWithRole = { ...newUser, role: 'user' };
              await permissionManager.updateUserRoleWithRetry('user', newUser.id);
              
              logAuthEvent('ROLE_SYNC_FALLBACK', {
                userId: newUser.id,
                fallbackRole: 'user',
                permissionSyncState: permissionManager.getSyncState()
              });
            }
          } catch (error) {
            logAuthEvent('ROLE_SYNC_ERROR', {
              userId: newUser.id,
              permissionSyncState: permissionManager.getSyncState()
            }, error);
            
            // Still keep the user even if role fetch fails - fallback to current role or 'user'
            userWithRole = { 
              ...newUser, 
              role: user?.role || 'user' 
            };
            
            // Ensure permissions are at least set to a default state
            if (permissionManager.getSyncState() === SyncState.ERROR || 
                permissionManager.getSyncState() === SyncState.IDLE) {
              await permissionManager.updateUserRoleWithRetry(userWithRole.role as any, newUser.id);
            }
          }
        } else {
          // Use existing role if we have it and user ID hasn't changed
          userWithRole = user && user.id === newUser.id
            ? { ...newUser, role: user.role }
            : newUser;
        }
      } else if (newSession && newSession.user && !newUser) {
        // Edge case: we have session but no user provided
        // Fetch minimal user data from session
        userWithRole = {
          ...newSession.user,
          role: user?.role || 'user'
        };
      }
      
      // Check if we actually need to update
      const needsSessionUpdate = session !== newSession;
      const needsUserUpdate = !user || !userWithRole || 
                            user.id !== userWithRole.id || 
                            user.role !== userWithRole.role;
                              
      // Only update if something actually changed
      if (needsSessionUpdate || needsUserUpdate) {
        // Update session and user state together
        setSession(newSession);
        setUser(userWithRole);
        
        // Update auth state based on session existence
        authStateRef.current = newSession ? 'AUTHENTICATED' : 'UNAUTHENTICATED';
        
        // Log the state change
        logAuthEvent(
          'AUTH_STATE_UPDATED', 
          { 
            hasSession: !!newSession, 
            hasUser: !!userWithRole,
            newState: authStateRef.current,
            roleChanged,
            permissionSyncState: permissionManager.getSyncState(),
            permissionStateVersion: permissionManager.getStateVersion()
          }
        );
      } else {
        logAuthEvent('AUTH_STATE_UNCHANGED', {
          sessionExists: !!session,
          userExists: !!user
        });
      }
      
    } catch (error) {
      logAuthEvent('AUTH_STATE_UPDATE_ERROR', {
        hasSession: !!newSession,
        hasUser: !!newUser,
        permissionSyncState: permissionManager.getSyncState()
      }, error);
      
      // In case of error, make sure session and user are in a consistent state
      if (!newSession) {
        setSession(null);
        setUser(null);
        permissionManager.clearPermissions();
        authStateRef.current = 'ERROR';
      }
    }
  }, [user, session, logAuthEvent, fetchUserRole, getCachedRole]);
  
  // Process pending auth events in sequence to avoid race conditions
  const processNextEvent = useCallback(async () => {
    if (!isActive.current || processingEventRef.current || pendingEventsRef.current.length === 0) {
      return;
    }
    
    processingEventRef.current = true;
    const event = pendingEventsRef.current.shift();
    
    if (!event) {
      processingEventRef.current = false;
      return;
    }
    
    logAuthEvent(`PROCESSING_EVENT`, { 
      type: event.type, 
      currentState: authStateRef.current,
      pendingEvents: pendingEventsRef.current.length,
      requestId: event.requestId
    });
    
    try {
      switch (event.type) {
        case 'INITIALIZE':
          await handleInitialize();
          break;
        case 'SIGN_IN':
          await handleSignIn(event.data);
          break;
        case 'SIGN_OUT':
          await handleSignOut();
          break;
        case 'REFRESH':
          await handleRefresh(event.data);
          break;
        case 'USER_UPDATED':
          await handleUserUpdated(event.data);
          break;
      }
    } catch (error) {
      logAuthEvent('EVENT_PROCESSING_ERROR', { eventType: event.type }, error);
      
      // For critical errors, clear auth state
      if (event.type === 'INITIALIZE' || event.type === 'REFRESH') {
        await updateAuthState(null, null);
      }
    } finally {
      processingEventRef.current = false;
      
      // Process next event if any
      if (pendingEventsRef.current.length > 0) {
        processNextEvent();
      } else if (event.type === 'INITIALIZE' || event.type === 'REFRESH') {
        // Only set loading to false when initialization or refresh completes
        setIsLoading(false);
      }
    }
  }, [logAuthEvent, updateAuthState]);

  // Add event to queue and process
  const queueEvent = useCallback((type: AuthEvent, data?: any) => {
    if (!isActive.current) return;
    
    const userId = data?.user?.id || user?.id || 'anonymous';
    const requestId = generateRequestId(type, userId);
    
    // Don't process duplicate or debounced requests
    if (!shouldProcessRequest(requestId, type)) {
      return;
    }
    
    pendingEventsRef.current.push({ type, data, requestId });
    logAuthEvent(`EVENT_QUEUED`, { type, queueLength: pendingEventsRef.current.length, requestId });
    
    // If no event is being processed, start processing
    if (!processingEventRef.current) {
      processNextEvent();
    }
  }, [logAuthEvent, processNextEvent, shouldProcessRequest, user]);

  // Check if the current URL has OAuth related parameters
  const isOAuthRedirect = useCallback(() => {
    const url = new URL(window.location.href);
    // Check for LinkedIn OIDC specific parameters or general OAuth parameters
    return (
      url.searchParams.has('code') || 
      url.hash.includes('access_token=') || 
      url.hash.includes('id_token=')
    );
  }, []);

  // Handler for initialization with improved OAuth redirect handling
  const handleInitialize = useCallback(async () => {
    if (!isActive.current || initializedRef.current) return;
    
    // New flag to check if this is an OAuth redirect
    const isHandlingOAuthRedirect = isOAuthRedirect();
    if (isHandlingOAuthRedirect) {
      oauthRedirectInProgressRef.current = true;
    }
    
    authStateRef.current = 'INITIALIZING';
    logAuthEvent('INITIALIZATION_STARTED', { isOAuthRedirect: isHandlingOAuthRedirect });
    
    try {
      // IMPORTANT FIX: First get the session, and only then get the user
      // This ensures we have a valid session before trying to get the user
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      // Early return if component unmounted during async operation
      if (!isActive.current) return;
      
      // Handle session fetch error - critical error
      if (sessionError) {
        logAuthEvent('SESSION_FETCH_ERROR', {}, sessionError);
        throw new Error(`Session fetch error: ${sessionError.message}`);
      }
      
      // Check if we have a valid session
      if (!sessionData?.session) {
        logAuthEvent('NO_VALID_SESSION');
        
        // If this is an OAuth redirect but we don't have a session,
        // it may be that the session is still being established
        if (isHandlingOAuthRedirect) {
          logAuthEvent('OAUTH_REDIRECT_WITHOUT_SESSION');
          
          // For OAuth redirects without a session, we could implement a retry mechanism
          if (retryCountRef.current < 3) {
            logAuthEvent('RETRYING_SESSION_FETCH', { attempt: retryCountRef.current + 1 });
            retryCountRef.current++;
            
            // Wait a bit and try again - the session might still be establishing
            setTimeout(() => {
              if (isActive.current) {
                queueEvent('INITIALIZE');
              }
            }, 1000); // Wait 1 second before retrying
            
            return;
          } else {
            // After retries, if we still don't have a session, it's likely a genuine error
            logAuthEvent('OAUTH_SESSION_RETRIEVAL_FAILED');
            throw new Error("Failed to retrieve session after OAuth redirect");
          }
        } else {
          // Not an OAuth redirect, just no session exists
          await updateAuthState(null, null);
        }
      } else {
        // We have a valid session, now fetch the user data
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        // Early return if component unmounted during async operation
        if (!isActive.current) return;
        
        // Handle user fetch error
        if (userError) {
          logAuthEvent('USER_FETCH_ERROR', {}, userError);
          
          // If this is an OAuth redirect, user data might not be immediately available
          if (isHandlingOAuthRedirect && retryCountRef.current < 3) {
            logAuthEvent('RETRYING_USER_FETCH', { attempt: retryCountRef.current + 1 });
            retryCountRef.current++;
            
            // Try again with the session we already have
            setTimeout(() => {
              if (isActive.current) {
                queueEvent('REFRESH', sessionData.session);
              }
            }, 1000);
            
            return;
          } else {
            // If we have a session but no user, we can still try to use the session data
            logAuthEvent('USING_SESSION_USER_DATA');
            await updateAuthState(sessionData.session, sessionData.session.user);
          }
        } else if (userData?.user) {
          // Success case: Valid session and user
          if (sessionData.session.user.id !== userData.user.id) {
            throw new Error("Session and user ID mismatch, resetting auth state");
          }
          
          logAuthEvent('VALID_SESSION_AND_USER', { userId: userData.user.id });
          
          // Clear any old authentication data for this user
          if (isHandlingOAuthRedirect) {
            logAuthEvent('CLEARING_OLD_AUTH_DATA_FOR_OAUTH');
            // Clear stale data before setting new state for OAuth
            sessionStorage.removeItem(ROLE_CACHE_KEY);
          }
          
          await updateAuthState(sessionData.session, userData.user, true);
          
          // If this was an OAuth redirect, navigate after successful authentication
          if (isHandlingOAuthRedirect && oauthRedirectInProgressRef.current) {
            logAuthEvent('SUCCESSFUL_OAUTH_REDIRECT_HANDLED');
            oauthRedirectInProgressRef.current = false;
          }
        } else {
          // Edge case: Session exists but no user data
          logAuthEvent("Session exists but couldn't get user data");
          await updateAuthState(sessionData.session, sessionData.session.user);
        }
      }
      
      initializedRef.current = true;
      retryCountRef.current = 0; // Reset retry counter
      logAuthEvent('INITIALIZATION_COMPLETED', { 
        hasSession: !!sessionData?.session,
      });
    } catch (error: any) {
      logAuthEvent('INITIALIZATION_FAILED', {
        isOAuthRedirect: isHandlingOAuthRedirect,
        retryCount: retryCountRef.current
      }, error);
      
      // Reset auth state on critical errors
      await updateAuthState(null, null);
      
      if (isActive.current) {
        // Only show toast for non-OAuth errors or after retries fail
        if (!isHandlingOAuthRedirect || retryCountRef.current >= 3) {
          toast({
            title: "Authentication Error",
            description: "Failed to initialize authentication. Please try again later.",
            variant: "destructive",
          });
        }
      }
      
      initializedRef.current = true; // Mark as initialized even on error
      retryCountRef.current = 0; // Reset retry counter
    } finally {
      // Reset OAuth redirect flag if it was set
      if (oauthRedirectInProgressRef.current) {
        oauthRedirectInProgressRef.current = false;
      }
    }
  }, [updateAuthState, toast, logAuthEvent, queueEvent, isOAuthRedirect]);

  // Handle sign in event with improved permission syncing
  const handleSignIn = useCallback(async (newSession: Session) => {
    if (!isActive.current) return;
    
    logAuthEvent('SIGN_IN_EVENT_HANDLING', { 
      sessionId: newSession.user.id,
      permissionSyncState: permissionManager.getSyncState()
    });
    
    try {
      // Clear any previous role cache for oauth
      sessionStorage.removeItem(ROLE_CACHE_KEY);
      
      // Get user data
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (userData?.user) {
        // Update auth state with new session and user
        await updateAuthState(newSession, userData.user, true);
        
        // Refresh user-specific cache
        queryClient.invalidateQueries({ queryKey: ['user'] });
        
        logAuthEvent('SIGN_IN_EVENT_COMPLETED', { 
          userId: userData.user.id,
          permissionSyncState: permissionManager.getSyncState(),
          permissionStateVersion: permissionManager.getStateVersion()
        });
      } else {
        // Session exists but no user - handle edge case
        logAuthEvent('SIGN_IN_EVENT_NO_USER', {
          permissionSyncState: permissionManager.getSyncState()
        });
        await updateAuthState(newSession, null);
      }
    } catch (error) {
      logAuthEvent('SIGN_IN_EVENT_ERROR', {
        permissionSyncState: permissionManager.getSyncState()
      }, error);
      
      // Try to maintain the session at least
      await updateAuthState(newSession, null);
    }
  }, [updateAuthState, queryClient, logAuthEvent]);

  // Handle user updated event with improved permission syncing
  const handleUserUpdated = useCallback(async (newSession: Session) => {
    if (!isActive.current) return;
    
    logAuthEvent('USER_UPDATED_EVENT_HANDLING', { 
      userId: newSession.user.id,
      permissionSyncState: permissionManager.getSyncState()
    });
    
    try {
      // Get updated user data
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (userData?.user) {
        // Update auth state with new session and user
        await updateAuthState(newSession, userData.user, true);
        
        // Refresh user-specific data
        queryClient.invalidateQueries({ queryKey: ['user'] });
        
        logAuthEvent('USER_UPDATED_EVENT_COMPLETED', { 
          userId: userData.user.id,
          permissionSyncState: permissionManager.getSyncState(),
          permissionStateVersion: permissionManager.getStateVersion()
        });
      } else {
        // Session exists but no user - handle edge case
        logAuthEvent('USER_UPDATED_EVENT_NO_USER', {
          permissionSyncState: permissionManager.getSyncState()
        });
        await updateAuthState(newSession, null);
      }
    } catch (error) {
      logAuthEvent('USER_UPDATED_EVENT_ERROR', {
        permissionSyncState: permissionManager.getSyncState()
      }, error);
      await updateAuthState(newSession, null);
    }
  }, [updateAuthState, queryClient, logAuthEvent]);

  // Handle refresh token event with improved permission syncing
  const handleRefresh = useCallback(async (newSession: Session) => {
    if (!isActive.current) return;
    
    logAuthEvent('TOKEN_REFRESH_EVENT_HANDLING', { 
      userId: newSession.user.id,
      permissionSyncState: permissionManager.getSyncState()
    });
    
    try {
      // Get user data with refreshed token
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      if (userData?.user) {
        // Only fetch role if it's been some time since the last update or we don't have a role
        const shouldFetchRole = !user?.role || 
          permissionManager.getSyncState() === SyncState.ERROR || 
          permissionManager.getSyncState() === SyncState.IDLE;
          
        // Update auth state with new session and user
        await updateAuthState(newSession, userData.user, shouldFetchRole);
        
        logAuthEvent('TOKEN_REFRESH_EVENT_COMPLETED', { 
          userId: userData.user.id,
          roleFetched: shouldFetchRole,
          permissionSyncState: permissionManager.getSyncState(),
          permissionStateVersion: permissionManager.getStateVersion()
        });
      } else {
        // Session exists but no user - handle edge case
        logAuthEvent('TOKEN_REFRESH_EVENT_NO_USER', {
          permissionSyncState: permissionManager.getSyncState()
        });
        await updateAuthState(newSession, null);
      }
    } catch (error) {
      logAuthEvent('TOKEN_REFRESH_EVENT_ERROR', {
        permissionSyncState: permissionManager.getSyncState()
      }, error);
      await updateAuthState(newSession, null);
    }
  }, [updateAuthState, logAuthEvent, user]);

  // Enhanced handle sign out event with improved resource cleanup and error handling
  const handleSignOut = useCallback(async () => {
    if (!isActive.current) return;
    
    logAuthEvent('SIGN_OUT_EVENT_HANDLING', {
      permissionSyncState: permissionManager.getSyncState(),
      permissionStateVersion: permissionManager.getStateVersion()
    });
    
    try {
      // 1. Clear all loading states first to prevent pending operations
      LoadingStateManager.getInstance().clearAllRequests();
      
      // 2. Clear user-specific caches before signing out
      if (cacheManager.clearUserSpecificCache) {
        cacheManager.clearUserSpecificCache();
      }
      
      // 3. Clear all query cache to prevent stale data
      queryClient.clear();
      
      // 4. Clear permissions completely
      permissionManager.clearPermissions();
      
      // 5. Clear all auth-related storage
      clearAuthStorage();
      
      // 6. Clear role cache
      sessionStorage.removeItem(ROLE_CACHE_KEY);
      
      // 7. Clear auth state before performing the actual sign out
      // This prevents flashing of outdated user data during signout process
      await updateAuthState(null, null);
      
      // 8. Log successful cleanup
      logAuthEvent('AUTH_STATE_CLEANUP_COMPLETED', {
        permissionSyncState: permissionManager.getSyncState(),
        permissionStateVersion: permissionManager.getStateVersion()
      });
    } catch (error) {
      // Handle any unexpected errors during cleanup
      logAuthEvent('AUTH_STATE_CLEANUP_ERROR', {}, error);
      // Continue with sign out even if cleanup fails
    }
    
    // Log completion of the sign out process
    logAuthEvent('SIGN_OUT_EVENT_COMPLETED', {
      permissionSyncState: permissionManager.getSyncState(),
      permissionStateVersion: permissionManager.getStateVersion()
    });
  }, [updateAuthState, queryClient, logAuthEvent]);

  // Initialize auth state on mount with improved lifecycle management
  useEffect(() => {
    isActive.current = true;
    initializedRef.current = false;
    authStateRef.current = 'IDLE';
    
    // Queue initialization as the first event
    queueEvent('INITIALIZE');
    
    // Set up auth state listener with improved event handling
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isActive.current) return;
        
        logAuthEvent(`AUTH_STATE_CHANGED: ${event}`, { 
          hasSession: !!newSession,
          currentState: authStateRef.current,
          isInitialized: initializedRef.current
        });
        
        // Skip events if we're still initializing
        if (!initializedRef.current) {
          logAuthEvent('Skipping auth state change during initialization');
          return;
        }
        
        // Map Supabase events to our event queue
        switch (event) {
          case "SIGNED_OUT":
            queueEvent('SIGN_OUT');
            break;
          case "SIGNED_IN":
            if (newSession) {
              queueEvent('SIGN_IN', newSession);
            }
            break;
          case "TOKEN_REFRESHED":
            if (newSession) {
              queueEvent('REFRESH', newSession);
            }
            break;
          case "USER_UPDATED":
            if (newSession) {
              queueEvent('USER_UPDATED', newSession);
            }
            break;
        }
      }
    );

    // Cleanup function with improved state reset
    return () => {
      isActive.current = false;
      
      // Clear all refs
      pendingEventsRef.current = [];
      processingEventRef.current = false;
      
      // Clear any pending timeouts
      if (roleUpdateTimeoutRef.current) {
        clearTimeout(roleUpdateTimeoutRef.current);
        roleUpdateTimeoutRef.current = null;
      }
      
      // Unsubscribe from auth listener
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
      
      logAuthEvent('AUTH_PROVIDER_UNMOUNTED');
    };
  }, [queueEvent, logAuthEvent]);

  // Improved session refresh handlers
  const handleRefreshSuccess = useCallback((newSession: Session) => {
    logAuthEvent('SESSION_REFRESH_SUCCESS', { 
      userId: newSession.user.id,
      expiresAt: newSession.expires_at
    });
    
    setLastRefreshed(new Date());
    
    // Update auth state with new session and user data
    // No need to re-fetch role on every refresh
    updateAuthState(newSession, newSession.user, false);
  }, [logAuthEvent, updateAuthState]);
  
  const handleRefreshError = useCallback(() => {
    logAuthEvent('SESSION_REFRESH_FAILURE');
    
    // Reset auth state on critical refresh errors
    updateAuthState(null, null);
    
    // Force sign out when session can't be refreshed
    signOut();
  }, [logAuthEvent, updateAuthState]);
  
  // Integrate the enhanced session refresh hook
  const { refreshSession: sessionRefresh, status: refreshStatus } = useSessionRefresh(
    session,
    handleRefreshSuccess,
    handleRefreshError
  );

  // Exposed method for manual signIn
  const signIn = async (email: string, password: string) => {
    try {
      logAuthEvent('SIGN_IN_ATTEMPT', { email });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const errorType = mapErrorToType(error);
        const errorDetails = getErrorMessage(errorType);
        
        logAuthEvent('SIGN_IN_FAILURE', { email, errorType }, error);
        
        toast({
          title: errorDetails.title,
          description: `${errorDetails.description} ${errorDetails.action || ''}`,
          variant: "destructive",
        });
        
        return { error, errorType };
      }

      logAuthEvent('SIGN_IN_SUCCESS', { email });
      return undefined;
    } catch (error: any) {
      logAuthEvent('SIGN_IN_EXCEPTION', { email }, error);
      
      toast({
        title: "Authentication Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      
      return { error };
    }
  };

  // Exposed method for manual signUp
  const signUp = async (email: string, password: string) => {
    try {
      logAuthEvent('SIGN_UP_ATTEMPT', { email });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        const errorType = mapErrorToType(error);
        const errorDetails = getErrorMessage(errorType);
        
        logAuthEvent('SIGN_UP_FAILURE', { email, errorType }, error);
        
        toast({
          title: errorDetails.title,
          description: `${errorDetails.description} ${errorDetails.action || ''}`,
          variant: "destructive",
        });
        
        return { error, errorType };
      }

      logAuthEvent('SIGN_UP_SUCCESS', { email });
      
      toast({
        title: "Registration Successful",
        description: "Please check your email to verify your account.",
      });
      
      return undefined;
    } catch (error: any) {
      logAuthEvent('SIGN_UP_EXCEPTION', { email }, error);
      
      toast({
        title: "Registration Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      
      return { error };
    }
  };

  // Exposed method for LinkedIn sign-in
  const signInWithLinkedIn = async () => {
    try {
      logAuthEvent('LINKEDIN_SIGN_IN_ATTEMPT');
      
      // Clear role cache to ensure fresh role fetch after OAuth
      sessionStorage.removeItem(ROLE_CACHE_KEY);
      
      // Use the correct provider name 'linkedin_oidc' instead of 'linkedin'
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: window.location.origin + '/auth',
          // Add scopes to request specific info
          scopes: 'openid profile email',
        },
      });

      if (error) {
        const errorType = mapErrorToType(error);
        const errorDetails = getErrorMessage(errorType);
        
        logAuthEvent('LINKEDIN_SIGN_IN_FAILURE', { errorType }, error);
        
        toast({
          title: errorDetails.title,
          description: `${errorDetails.description} ${errorDetails.action || ''}`,
          variant: "destructive",
        });
        
        return { error, errorType };
      }

      logAuthEvent('LINKEDIN_REDIRECT_INITIATED', {
        redirectUrl: data?.url || 'unknown'
      });
      return undefined;
    } catch (error: any) {
      logAuthEvent('LINKEDIN_SIGN_IN_EXCEPTION', {}, error);
      
      toast({
        title: "Authentication Error",
        description: "Unable to sign in with LinkedIn. Please try again later.",
        variant: "destructive",
      });
      
      return { error };
    }
  };

  // Exposed method for sign out
  const signOut = async () => {
    logAuthEvent('SIGN_OUT_ATTEMPT');
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logAuthEvent('SIGN_OUT_API_ERROR', {}, error);
        toast({
          title: "Sign Out Error",
          description: "There was a problem signing out. Please try again.",
          variant: "destructive",
        });
      } else {
        logAuthEvent('SIGN_OUT_API_SUCCESS');
        // The auth state listener will handle the actual sign out process
      }
    } catch (error) {
      logAuthEvent('SIGN_OUT_EXCEPTION', {}, error);
      // Proceed with local cleanup anyway
      queueEvent('SIGN_OUT');
    }
  };

  // Exposed method for manual session refresh
  const refreshSession = async () => {
    if (!session) {
      logAuthEvent('REFRESH_SESSION_SKIPPED', { reason: 'No active session' });
      return;
    }
    
    logAuthEvent('REFRESH_SESSION_ATTEMPT');
    await sessionRefresh();
  };

  // Define the current user auth state
  const isAuthenticated = !!session && !!user;

  // Context value
  const value = {
    session,
    user,
    isLoading,
    isAuthenticated,
    signIn,
    signUp,
    signInWithLinkedIn,
    signOut,
    refreshSession,
    lastRefreshed
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Export the hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};
